import { FC, useEffect, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ImageUploadBox } from '@service/seller/components/ImageUploadBox';
import {
  giftChoiceUnitDataAtomFamily,
  giftChoiceUnitListDataAtom,
  giftChoiceUnitStatusAtomFamily,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import {
  createGiftChoiceUnit,
  updateGiftChoiceUnit,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.controller';
import { useGiftChoiceUnitHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { TextField } from '@shared/components/TextField';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface FormData {
  giftItemBrand: string;
  name: string;
  description: string;
  priceRegular: number;
  priceWhole: number;
  enabled: boolean;
  validPeriod: string;
  placeOfExchange: string;
}

export const GiftChoiceUnitFormPage: FC = function GiftChoiceUnitFormPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { sellerRole } = useAuth();

  const isEditMode = Boolean(unitId);

  // 수정 모드일 때 데이터 조회
  useGiftChoiceUnitHook(unitId ?? '', sellerRole(), !isEditMode);
  useGiftItemBrandListHook(sellerRole());

  const status = useRecoilValue(giftChoiceUnitStatusAtomFamily(unitId ?? ''));
  const unitData = useRecoilValue(giftChoiceUnitDataAtomFamily(unitId ?? ''));
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 목록 데이터 업데이트용
  const setListData = useSetRecoilState(giftChoiceUnitListDataAtom);

  // 폼 상태
  const [formData, setFormData] = useState<FormData>({
    giftItemBrand: searchParams.get('brandId') || '',
    name: '',
    description: '',
    priceRegular: 0,
    priceWhole: 0,
    enabled: true,
    validPeriod: '',
    placeOfExchange: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 수정 모드일 때 기존 데이터 로드
  useEffect(() => {
    if (isEditMode && unitData) {
      setFormData({
        giftItemBrand:
          typeof unitData.giftItemBrand === 'object'
            ? unitData.giftItemBrand?._id || ''
            : unitData.giftItemBrand || '',
        name: unitData.name || '',
        description: unitData.description || '',
        priceRegular: unitData.priceRegular || 0,
        priceWhole: unitData.priceWhole || 0,
        enabled: unitData.enabled !== false,
        validPeriod: unitData.couponDetail?.validPeriod || '',
        placeOfExchange: unitData.couponDetail?.placeOfExchange || '',
      });
    }
  }, [isEditMode, unitData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (['priceRegular', 'priceWhole'].includes(name)) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) || 0 }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('유닛명을 입력해주세요.');
      return;
    }

    try {
      setIsSubmitting(true);

      let imageId: string | undefined;

      // 이미지 업로드
      if (imageFile) {
        const uploadedImage = await postImage(imageFile, sellerRole());
        imageId = uploadedImage._id;
      }

      const payload = {
        giftItemBrand: formData.giftItemBrand || undefined,
        name: formData.name,
        description: formData.description || undefined,
        priceRegular: formData.priceRegular || undefined,
        priceWhole: formData.priceWhole || undefined,
        enabled: formData.enabled,
        image: imageId,
        couponDetail:
          formData.validPeriod || formData.placeOfExchange
            ? {
                validPeriod: formData.validPeriod || undefined,
                placeOfExchange: formData.placeOfExchange || undefined,
              }
            : undefined,
      };

      if (isEditMode && unitId) {
        // 수정
        const updated = await updateGiftChoiceUnit(unitId, payload, sellerRole());
        setListData((prev) => prev.map((item) => (item._id === unitId ? updated : item)));
        alert('수정되었습니다.');
      } else {
        // 생성
        const created = await createGiftChoiceUnit(payload, sellerRole());
        setListData((prev) => [...prev, created]);
        alert('등록되었습니다.');
      }

      navigate('/gift-seller/choice-unit');
    } catch (error) {
      console.error(error);
      alert(isEditMode ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 로딩 중 (수정 모드)
  if (isEditMode && (status === 'loading' || status === 'idle')) {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100"
          >
            ←
          </button>
          <h1 className="text-xl font-bold">선택유닛 {isEditMode ? '수정' : '등록'}</h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="mx-auto max-w-2xl p-4">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* 왼쪽: 정보 */}
            <div className="space-y-4">
              {/* 소속 브랜드 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  소속 브랜드
                </label>
                <select
                  name="giftItemBrand"
                  value={formData.giftItemBrand}
                  onChange={handleChange}
                  className="w-full rounded border px-3 py-2 text-sm"
                  disabled={brandListStatus !== 'success'}
                >
                  <option value="">브랜드 선택 (선택사항)</option>
                  {brandList.map((brand) => (
                    <option key={brand._id} value={brand._id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
                <p className="mt-0.5 text-xs text-gray-400">
                  이 선택유닛이 속한 브랜드를 선택하세요.
                </p>
              </div>

              {/* 판매 상태 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  판매 상태
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, enabled: !prev.enabled }))
                  }
                  className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    formData.enabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.enabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className={`ml-2 text-sm font-medium ${formData.enabled ? 'text-green-600' : 'text-red-500'}`}>
                  {formData.enabled ? '판매중' : '판매중단'}
                </span>
                <p className="mt-0.5 text-xs text-gray-400">
                  판매중단 시 쿠폰 상품에서 해당 유닛이 제외됩니다.
                </p>
              </div>

              {/* 유닛명 */}
              <TextField
                label="유닛명"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="예: ICE 아메리카노, 8500원 음료"
                required
              />

              {/* 설명 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  설명
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="선택유닛에 대한 설명 (선택사항)"
                  className="h-24 w-full rounded border px-3 py-2 text-sm"
                />
              </div>

              {/* 가격 설정 */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  가격 설정
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">정가</label>
                    <input
                      type="number"
                      name="priceRegular"
                      value={formData.priceRegular || ''}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-3 py-2 text-sm"
                    />
                    <p className="mt-0.5 text-xs text-gray-400">실제 상품 가격</p>
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">도매가</label>
                    <input
                      type="number"
                      name="priceWhole"
                      value={formData.priceWhole || ''}
                      onChange={handleChange}
                      placeholder="0"
                      className="w-full rounded border px-3 py-2 text-sm"
                    />
                    <p className="mt-0.5 text-xs text-gray-400">내부 관리용</p>
                  </div>
                </div>
              </div>

              {/* 쿠폰 상세 정보 */}
              <div className="border-t pt-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  쿠폰 상세 정보 (선택)
                </label>
                <div className="space-y-3">
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">유효기간</label>
                    <input
                      type="text"
                      name="validPeriod"
                      value={formData.validPeriod}
                      onChange={handleChange}
                      placeholder="예: 발행일로부터 93일"
                      className="w-full rounded border px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-0.5 block text-xs text-gray-500">교환처</label>
                    <input
                      type="text"
                      name="placeOfExchange"
                      value={formData.placeOfExchange}
                      onChange={handleChange}
                      placeholder="예: 전국 스타벅스 매장"
                      className="w-full rounded border px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  쿠폰형 상품일 경우 입력하세요.
                </p>
              </div>
            </div>

            {/* 오른쪽: 이미지 */}
            <div>
              <div className="max-w-[250px]">
                <ImageUploadBox
                  title="유닛 이미지"
                  setFile={setImageFile}
                  defaultImageUrl={unitData?.image?.url}
                />
              </div>
              <p className="mt-1 text-xs text-gray-400">
                선택유닛을 대표하는 이미지를 업로드하세요.
              </p>
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="mt-4 flex gap-3">
          <Button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-3 font-medium text-gray-700"
          >
            취소
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-indigo-600 py-3 font-medium text-white disabled:opacity-50"
          >
            {isSubmitting ? '저장 중...' : isEditMode ? '수정하기' : '등록하기'}
          </Button>
        </div>
      </form>
    </div>
  );
};
